import React, { useState, useEffect } from 'react';

import axios from '../../../axios';
import BottomScrollListener from 'react-bottom-scroll-listener';

import HashtagInsightsCard from './HashtagInsightsCard';

import * as url from '../../../utils/url';
import { loginImgTrivvy, USER_CHATS_EMPTY_STATE_MSG } from '../../../utils/constants';
import { resolveOrgPrefix } from '../../../utils/stringUtils';

// bug903: fetches the accessible-hashtag list, mounts one HashtagInsightsCard
// per hashtag (see that file for per-card fetch/date-range/generate logic).
const HashtagInsights = ({ user }) => {
    const [hashtags, setHashtags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextSkip, setNextSkip] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        fetchHashtags(0);
    }, []);

    // Mirrors HashtagOverview.jsx's fetchHashtags(skip) pagination convention.
    const fetchHashtags = async (skipValue) => {
        try {
            if (skipValue === 0) setLoading(true);
            else setLoadingMore(true);

            const res = await axios.get(`${url.textifyListGroupInsights}?skip=${skipValue}`);
            const items = (res.data && res.data.items) || [];
            const moreAvailable = (res.data && res.data.hasMore !== undefined) ? res.data.hasMore : false;
            const resultingNextSkip = (res.data && res.data.nextSkip !== undefined) ? res.data.nextSkip : (skipValue + items.length);

            if (skipValue === 0) {
                setHashtags(items);
            } else {
                setHashtags((prev) => [...prev, ...items]);
            }
            setNextSkip(resultingNextSkip);
            setHasMore(moreAvailable);
        } catch (err) {
            console.error('[HashtagInsights] Error fetching hashtags:', err);
        } finally {
            if (skipValue === 0) setLoading(false);
            else setLoadingMore(false);
        }
    };

    const handleBottomScroll = () => {
        if (hasMore && !loading && !loadingMore) {
            fetchHashtags(nextSkip);
        }
    };

    const orgPrefix = resolveOrgPrefix(user);

    const isOrgAdmin = !!(user && user.profile && user.profile.orgAdmin);
    const showEmptyState = !loading && !isOrgAdmin && hashtags.length === 0;

    return (
        <div className="uc-insights-page">
            {/* No h1 here: the tab label above already says "Insights" — see the
                same redundancy fix on HashtagOverview.jsx's old heading. */}
            {!showEmptyState && (
                <div className="uc-header-area">
                    <p>AI-generated summaries for each hashtag channel, built from participant messages over a date range you choose.</p>
                </div>
            )}

            {showEmptyState && (
                <div className="uc-empty-state">
                    <img src={loginImgTrivvy} alt="" />
                    <div className="uc-empty-state-msg">{USER_CHATS_EMPTY_STATE_MSG}</div>
                </div>
            )}

            {!showEmptyState && (
                <BottomScrollListener offset={50} onBottom={handleBottomScroll}>
                    {(scrollRef) => (
                        <div
                            className="uc-insights-cards-scroll"
                            ref={(node) => {
                                if (typeof scrollRef === 'function') {
                                    scrollRef(node);
                                } else if (scrollRef && typeof scrollRef === 'object') {
                                    scrollRef.current = node;
                                }
                            }}
                        >
                            {loading ? (
                                <p className="text-center text-muted">Loading...</p>
                            ) : (
                                hashtags.map((item) => {
                                    const rawName = item.groupName || item.normalizedGroupName || '';
                                    const baseName = rawName.startsWith('#') ? rawName.slice(1) : rawName;
                                    const hashtagLabel = orgPrefix ? `${orgPrefix}-${baseName.toLowerCase()}` : baseName;
                                    return (
                                        <HashtagInsightsCard
                                            key={item._id}
                                            groupId={item._id}
                                            hashtagLabel={hashtagLabel}
                                            description={item.description}
                                            createdAt={item.createdAt}
                                        />
                                    );
                                })
                            )}
                            {loadingMore && (
                                <p className="text-center text-muted">Loading more...</p>
                            )}
                        </div>
                    )}
                </BottomScrollListener>
            )}
        </div>
    );
};

export default HashtagInsights;
